import React from "react";
import FamilyTreeNode from "./FamilyTreeNode";
import {FamilyTreeNodeStyle} from "../models/FamilyTreeType";
import "../../styles/components/_other-members-section.scss";

export type OtherMembersSectionNodeData = {
    otherMembers: Array<{
        id: string;
        firstName: string;
        lastName: string;
        age?: number;
        state?: string;
        onClick?: () => void;
        style?: FamilyTreeNodeStyle;
    }>;
    onAddOtherMembersClick?: () => void;
    isProfileWithProposalsOrArchived?: boolean;
    showAddOtherMembersButton?: boolean;
};

const OtherMembersSectionNode = ({data}: { data: OtherMembersSectionNodeData }) => {
    const otherMembers = data?.otherMembers ?? [];
    const hasMembers = otherMembers.length > 0;
    return (
        <div className="other-members-section">
            <div className="other-members-section__label">Other Members</div>
            <div className="other-members-section__members-outer">
                {hasMembers && (
                    <div className="other-members-section__members-row">
                        {otherMembers.map((member) => (
                            <div
                                key={member.id}
                            >
                                <FamilyTreeNode data={{
                                    firstName: member.firstName,
                                    lastName: member.lastName,
                                    age: member.age,
                                    state: member.state,
                                    onClick: member.onClick,
                                    style: member.style ?? FamilyTreeNodeStyle.DEFAULT
                                }}/>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {!data.isProfileWithProposalsOrArchived && data.showAddOtherMembersButton ? (
                <button
                    className="add-other-member-button"
                    onClick={data.onAddOtherMembersClick}
                    type="button"
                    data-testid="add-other-member-button"
                    aria-label="Add Other Member"
                >
                    <span className="add-other-member-button__icon" aria-hidden="true">add_circle_outline</span>
                    <span className="add-other-member-button__text">Add Other Member</span>
                </button>
            ) : (
                <div className="other-members-section__padding"/>
            )}
        </div>
    );
};

const areMembersEqual = (
    prevMember: OtherMembersSectionNodeData['otherMembers'][number],
    nextMember: OtherMembersSectionNodeData['otherMembers'][number]
) => prevMember.id === nextMember.id
    && prevMember.firstName === nextMember.firstName
    && prevMember.lastName === nextMember.lastName
    && prevMember.age === nextMember.age
    && prevMember.state === nextMember.state
    && prevMember.style === nextMember.style
    && prevMember.onClick === nextMember.onClick;

const areNodeFlagsEqual = (prev: OtherMembersSectionNodeData, next: OtherMembersSectionNodeData) => (
    prev.onAddOtherMembersClick === next.onAddOtherMembersClick
    && prev.isProfileWithProposalsOrArchived === next.isProfileWithProposalsOrArchived
    && prev.showAddOtherMembersButton === next.showAddOtherMembersButton
);

const areOtherMembersEqual = (prev: OtherMembersSectionNodeData, next: OtherMembersSectionNodeData) => {
    if (prev === next) return true;

    const prevMembers = prev.otherMembers ?? [];
    const nextMembers = next.otherMembers ?? [];

    if (prevMembers.length !== nextMembers.length) return false;

    const membersMatch = prevMembers.every((member, index) => areMembersEqual(member, nextMembers[index]));
    if (!membersMatch) return false;

    return areNodeFlagsEqual(prev, next);
};

export default React.memo(OtherMembersSectionNode, (prev, next) => areOtherMembersEqual(prev.data, next.data));
